import React, { useRef, useState, memo, useEffect } from "react";
import styled from "styled-components/macro";
import { ActiveFilter, Filter, PageWithFilterType } from "../types";
import { MdArrowDropDown } from "react-icons/md";
import FilterItem from "./FilterItem";
import { useDispatch, useSelector } from "react-redux";
import { getActiveFiltersByName } from "../utils";
import { DefaultText, shadow } from "theme/GlobalElements";
import { colors } from "theme/Theme";
import {
  selectActiveFilters,
  selectOpenedFilter,
  setOpenedFilter,
} from "../filtersSlice";
import { SortingType } from "pages/category/types";
import { useSetFilters } from "hooks/useSetFilters";

interface FilterRowProps {
  filter: Filter;
  pageType: PageWithFilterType;
  fetchFN: (
    activefilters: ActiveFilter[],
    page: number,
    sortingType: SortingType
  ) => void;
}
export const filtersToQuery = (activeFilter: ActiveFilter[]) => {
  return activeFilter.map((filter) => {
    if (filter.activeFilters.length !== 0) {
      return `${filter.name}=${filter.activeFilters.map(
        (filter: any) => filter !== "" && encodeURIComponent(filter)
      )}`;
    }
  });
};

const FilterRow: React.FC<FilterRowProps> = ({ filter, pageType, fetchFN }) => {
  const filterItemsRef = useRef<HTMLDivElement>(null);
  const [contentHeight, setContentHeight] = useState<string>("0px");
  const activeFilters = useSelector(selectActiveFilters);

  const openedFilter = useSelector(selectOpenedFilter);

  const [isOpen, setIsOpen] = useState<boolean>(false);

  let activeRowFilters = getActiveFiltersByName(filter.name, activeFilters);
  const dispatch = useDispatch();
  const setFilter = useSetFilters(filter.name, pageType, fetchFN);

  const expandRow = () => {
    if (isOpen) {
      dispatch(setOpenedFilter(""));
      return;
    }

    dispatch(setOpenedFilter(filter.name));
  };

  useEffect(() => {
    const isCurrentFilter = openedFilter === filter.name;
    setIsOpen(isCurrentFilter);
    setContentHeight(
      !isCurrentFilter ? "0px" : `${filterItemsRef.current?.scrollHeight}px`
    );
  }, [openedFilter, filter]);

  return (
    <FilterRowContainer>
      <FilterTitle onClick={expandRow}>
        <DefaultText
          fontSize={14}
          color={"white"}
          style={{ flexGrow: 2, fontWeight: "bold" }}
        >
          {filter.title} ({activeRowFilters.length})
        </DefaultText>
        <MdArrowDropDown className={`filter-arrow ${isOpen && "expanded"}`} />
      </FilterTitle>
      <FilterItems
        isOpen={isOpen}
        ref={filterItemsRef}
        style={{ maxHeight: `${contentHeight}` }}
      >
        {filter.availableFilters.map((filterDisplayName, idx) => {
          const isActive = activeRowFilters.includes(filterDisplayName);
          return (
            <FilterItem
              key={idx}
              text={filterDisplayName}
              isActive={isActive}
              setActive={() => setFilter(filterDisplayName)}
            />
          );
        })}
      </FilterItems>
    </FilterRowContainer>
  );
};

const FilterRowContainer = styled.div`
  ${shadow};
  & + & {
    margin-top: 20px;
  }
`;
FilterRowContainer.displayName = "FilterRowContainer";

const FilterTitle = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  background-color: ${colors.darkest};
  padding: 10px;

  .filter-arrow {
    fill: white;
    width: 24px;
    height: 24px;
    transition: 0.3s all;
  }

  .expanded {
    transform: rotate(180deg);
  }
`;
FilterTitle.displayName = "FilterTitle";

const FilterItems = styled.div<{ isOpen: boolean }>`
  padding: ${(props) => (props.isOpen ? "10px" : 0)};
  background-color: ${colors.gray};
  overflow: hidden;
  max-height: 0;
  opacity: ${(props) => (props.isOpen ? 1 : 0)};
  transition: 0.3s all;
`;
FilterItems.displayName = "FilterItems";

export default memo(FilterRow);
